import { AsyncPipe, DatePipe } from '@angular/common';
import { Component, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import type { SaveDraftItem } from '@shared/components/save-draft-modal/save-draft-modal.model';
import { resolveDraftIdsToDelete } from '@shared/helpers/draft-ids.helper';
import { flowMapper, FlowTypes } from '@shared/models';
import { ContentPageId } from '@shared/models/content-page-ids';
import { Routes } from '@shared/models/routes.enum';
import { LoggerCodes, LoggerService } from '@shared/modules/logger/logger';
import { WindowRef } from '@shared/modules/window-ref/window-ref';
import { DeleteDrafts, SaveDraftOnQuit } from '@store/actions/draft/draft.actions';
import { rejectDAOffer } from '@store/actions/open-choice/open-choice.actions';
import { StartOverFlow } from '@store/actions/start-over/start-over.actions';
import { UpdateFlowTypeSelection } from '@store/actions/triage/triage.actions';
import * as fromRootStore from '@store/index';
import {
  ClearStateAndStorage,
  selectInheritanceDecedentSecurityToggle,
  ShowSurvey,
  TrackCallToAction,
  TrackProcessAbandon,
} from '@store/index';
import {
  selectDraftId,
  selectDrafts,
  selectSaveDraftEnabledForFlow,
} from '@store/selectors/draft/draft.selectors';
import { selectRouterState } from '@store/selectors/router/common/router.selectors';
import { flowTypeSelection } from '@store/selectors/triage/triage.selectors';
import { ButtonComponent, ButtonModule } from '@vg-constellation/angular-16/button';
import { LinkModule } from '@vg-constellation/angular-16/link';
import { ModalDialogComponent } from '@vg-constellation/angular-16/modal-dialog';
import * as _ from 'lodash';
import { combineLatest, withLatestFrom } from 'rxjs';

import { getConfigClientFromRTE } from '../../../core/config/config-client';
import { PageComponent } from '../page/page.component';

@Component({
  selector: 'app-cancel',
  templateUrl: './cancel.component.html',
  styleUrls: ['./cancel.component.scss'],
  standalone: true,
  imports: [ModalDialogComponent, ButtonComponent, AsyncPipe, DatePipe, ButtonModule, LinkModule],
})
export class CancelComponent extends PageComponent implements OnInit, OnDestroy {
  quitUrl = '';
  content: any;
  config: any;
  currentPage = '';
  isQuitModalOpen = false;
  isSaveDraftEnabled: boolean = false;
  currentFlow!: FlowTypes;
  processStep!: string;
  processType!: string;
  currentDate = new Date().setDate(new Date().getDate() + 30);
  isSaveAndQuitModalOpen: boolean = false;
  isInheritanceDecedentSecurityToggleEnabled: boolean = false;

  private currentDraftId: string | null = null;
  private currentDrafts: SaveDraftItem[] = [];

  @Input() isMultiSigner: boolean = false;
  @Input() isOpenChoice: boolean = false;
  @Input() quitButtonVariant: string = 'secondary';
  @Input() isTransparent: boolean = false;

  @ViewChild('quitModal') quitModal?: ModalDialogComponent;
  @ViewChild('saveAndQuitModal') saveAndQuitModal?: ModalDialogComponent;

  @HostListener('window:message', ['$event'])
  onMessage(event: any): void {
    try {
      let dataObj = null;
      if ('data' in event) {
        dataObj = typeof event.data === 'object' ? event.data : JSON.parse(event.data);
      }
      if (!dataObj) return;

      if (
        dataObj.action === 'MDigital_Form_Close_Submitted' ||
        dataObj.action === 'MDigital_Form_Close_No_Submit' ||
        dataObj.action === 'MDigital_ThankYou_Close'
      ) {
        this.windowRef.nativeWindow.location.href = this.quitUrl;
      }
    } catch (e) {
      this.logger.error(
        { message: `Could not parse message data: ${e}` },
        LoggerCodes.WINDOW_MESSAGE_PARSE_FAILURE,
      );
    }
  }

  get labelText(): string {
    return this.shouldUseSaveAndQuitModal
      ? this.content.saveAndQuit.linkTextSaveAndQuit
      : this.content.linkText;
  }

  get startOverRouterLink(): string {
    const multiDecedentInfoLink = this.isInheritanceDecedentSecurityToggleEnabled
      ? Routes.MULTI_INHERITANCE_DECEDENT_INFORMATION
      : Routes.MULTI_INHERITANCE_DECEDENT_INFO;
    return this.currentFlow === FlowTypes.MULTI_INHERITANCE
      ? multiDecedentInfoLink
      : this.content.inheritancePersonalInfoLink;
  }

  get isMultiInheritance(): boolean {
    return this.currentFlow === FlowTypes.MULTI_INHERITANCE;
  }

  get shouldUseSaveAndQuitModal(): boolean {
    return this.isSaveDraftEnabled && this.isMultiInheritance;
  }

  constructor(
    globalStore: Store<fromRootStore.State>,
    protected logger: LoggerService,
    private windowRef: WindowRef,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    super(ContentPageId.cancel, globalStore);
  }

  ngOnInit() {
    super.ngOnInit();

    this.subs.add(
      this.content$.subscribe((content) => {
        this.content = content;
      }),
    );

    this.config = getConfigClientFromRTE();
    this.quitUrl = this.config.balances_url;

    this.subs.add(
      this.globalStore.select(selectRouterState).subscribe((routeData) => {
        _.attempt(() => {
          this.currentPage = routeData.url;
        });
      }),
    );

    this.subs.add(
      this.route.data
        .pipe(withLatestFrom(this.globalStore.select(flowTypeSelection)))
        .subscribe(([routeData, flowType]) => {
          this.currentFlow = flowType;
          const adobeFlowType = flowType ? flowMapper[flowType] : routeData.flow;
          this.processStep = `coo-${adobeFlowType}-${routeData.pageId}${this.isMultiSigner ? '-multi_signer' : ''}`;
          this.processType = `coo-${adobeFlowType}-flow`;
        }),
    );

    this.subs.add(
      combineLatest([
        this.globalStore.select(selectSaveDraftEnabledForFlow),
        this.globalStore.select(selectInheritanceDecedentSecurityToggle),
      ]).subscribe(([isSaveDraftEnabled, isInheritanceDecedentSecurityToggleEnabled]) => {
        this.isSaveDraftEnabled = isSaveDraftEnabled;
        this.isInheritanceDecedentSecurityToggleEnabled =
          isInheritanceDecedentSecurityToggleEnabled;
      }),
    );

    this.subs.add(
      combineLatest([
        this.globalStore.select(selectDraftId),
        this.globalStore.select(selectDrafts),
      ]).subscribe(([draftId, drafts]) => {
        this.currentDraftId = draftId;
        this.currentDrafts = drafts;
      }),
    );
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if (this.isQuitModalOpen) {
      this.quitModal?.closeDialogModal();
    }
    if (this.isSaveAndQuitModalOpen) {
      this.saveAndQuitModal?.closeDialogModal();
    }
  }

  openModal() {
    if (this.shouldUseSaveAndQuitModal) {
      this.openSaveAndQuitModal();
      return;
    }
    this.openQuitModal();
  }

  openQuitModal() {
    this.adobeTrackQuitPopupShown();
    this.isQuitModalOpen = true;
    this.quitModal?.openModalDialog();
  }

  openSaveAndQuitModal() {
    this.isSaveAndQuitModalOpen = true;
    this.saveAndQuitModal?.openModalDialog();
  }

  closeQuitModalIcon() {
    this.adobeTrackAbortedQuit();
    this.isQuitModalOpen = false;
    this.quitModal?.closeDialogModal();
  }

  closeQuitModalButton() {
    this.isQuitModalOpen = false;
    this.quitModal?.closeDialogModal();
  }

  closeSaveAndQuitModalIcon() {
    this.isSaveAndQuitModalOpen = false;
    this.saveAndQuitModal?.closeDialogModal();
  }

  closeSaveAndQuitModalButton() {
    _.attempt(() => {
      this.globalStore.dispatch(SaveDraftOnQuit());
      this.saveAndQuitModal?.closeDialogModal();
      this.redirectOnQuit();
    });
  }

  deleteDraftButton(): void {
    const ids = this.getIdsToDelete();
    if (ids.length > 0) {
      this.globalStore.dispatch(DeleteDrafts({ ids }));
    }
    this.isSaveAndQuitModalOpen = false;
    this.saveAndQuitModal?.closeDialogModal();
    this.redirectOnQuit();
  }

  yesQuitButton() {
    _.attempt(() => {
      this.adobeTrackYesQuit();
      this.globalStore.dispatch(ClearStateAndStorage());
      this.quitModal?.closeDialogModal();
      this.redirectOnQuit();
    });
  }

  startOverButtonClick(): void {
    const ids = this.getIdsToDelete();
    if (ids.length > 0) {
      this.globalStore.dispatch(DeleteDrafts({ ids }));
    }
    this.globalStore.dispatch(StartOverFlow());
    this.globalStore.dispatch(UpdateFlowTypeSelection({ payload: this.currentFlow }));
    this.saveAndQuitModal?.closeDialogModal();

    if (this.currentFlow === FlowTypes.MULTI_INHERITANCE) {
      this.router.navigate([
        this.isInheritanceDecedentSecurityToggleEnabled
          ? Routes.MULTI_INHERITANCE_DECEDENT_INFORMATION
          : Routes.MULTI_INHERITANCE_DECEDENT_INFO,
      ]);
      return;
    }
  }

  adobeTrackQuitPopupShown() {
    this.globalStore.dispatch(
      TrackCallToAction({
        location: this.processStep,
        ctaName: 'coo-quit-popup-shown',
        ctaType: 'button',
      }),
    );
  }

  adobeTrackAbortedQuit() {
    this.globalStore.dispatch(
      TrackCallToAction({
        location: this.processStep,
        ctaName: 'coo-quit-aborted',
        ctaType: 'button',
      }),
    );
  }

  adobeTrackYesQuit() {
    this.globalStore.dispatch(
      TrackProcessAbandon({
        processType: this.processType,
        processStep: this.processStep,
        processAbandonmentType: 'quit',
      }),
    );
  }

  showSurvey() {
    this.globalStore.dispatch(
      ShowSurvey({
        payload: {
          survey_name: getInputQuitSurvey(this.currentFlow),
        },
      }),
    );
  }

  private getIdsToDelete(): string[] {
    return resolveDraftIdsToDelete(this.currentDrafts, this.currentDraftId);
  }

  private redirectOnQuit() {
    if (this.isTransparent && this.isMultiInheritance) {
      this.router.navigate(['inheritance/overview']);
    } else if (this.isOpenChoice) {
      this.globalStore.dispatch(rejectDAOffer());
      this.globalStore.dispatch(fromRootStore.Go({ path: ['secure/minor-transfer/review'] }));
    } else {
      this.showSurvey();
    }
  }
}

function getInputQuitSurvey(flowType: FlowTypes) {
  switch (flowType) {
    case FlowTypes.MINOR:
      return 'quit_minor_inputpage';
    case FlowTypes.INTERNAL_TRANSFER:
      return 'quit_internal_transfer_inputpage';
    case FlowTypes.GIFTING:
      return 'quit_gifting_inputpage';
    case FlowTypes.INHERITANCE:
    default:
      return 'quit_inputpage';
  }
}
